import {Component, ElementRef, ViewChild, computed, effect, inject, signal} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {faBan, faPaperPlane, faTrash, faXmark} from '@fortawesome/free-solid-svg-icons';
import {DialogService} from '../../../services/dialog/dialog.service';
import {ViewContextService} from '../../../services/view-context/view-context.service';
import {DialogDebugService} from '../../../services/dialog/debug-mode.service';
import {ContextService} from '../../../services/context/context-signal.service';
import {ChatMessage, ClarifyOption, RecipeSuggestionItem} from '../../../services/dialog/dialog.models';
import {DecodedContextToken, decodeContextToken} from '../../../services/dialog/context-token';
import {ChatMessageComponent} from '../chat-message/chat-message.component';
import {UiToggleComponent, UiCodeEditorComponent} from '../../../shared/ui';

@Component({
  selector: 'app-dialog-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, FontAwesomeModule, ChatMessageComponent, UiToggleComponent, UiCodeEditorComponent],
  templateUrl: './dialog-panel.component.html',
  styleUrl: './dialog-panel.component.scss',
})
export class DialogPanelComponent {
  protected readonly dialogService = inject(DialogService);
  protected readonly viewContextService = inject(ViewContextService);
  protected readonly dialogDebug = inject(DialogDebugService);
  protected readonly contextService = inject(ContextService);

  @ViewChild('inputEl') private inputEl?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('messageList') private messageListEl?: ElementRef<HTMLDivElement>;

  draft = '';

  private lastFocusedElement: HTMLElement | null = null;
  private wasOpen = false;
  private wasSending = false;

  protected readonly faXmark = faXmark;
  protected readonly faPaperPlane = faPaperPlane;
  protected readonly faTrash = faTrash;
  protected readonly faBan = faBan;

  protected readonly examplePhrases = [
    'Entferne zwei Milch',
    'Was habe ich noch an Joghurt?',
    'Füge Butter zur Einkaufsliste hinzu',
  ];

  constructor() {
    effect(() => {
      const open = this.dialogService.isOpen();

      if (open && !this.wasOpen) {
        this.lastFocusedElement = document.activeElement as HTMLElement | null;
        setTimeout(() => this.inputEl?.nativeElement.focus(), 0);
      } else if (!open && this.wasOpen) {
        this.lastFocusedElement?.focus?.();
        this.lastFocusedElement = null;
      }
      this.wasOpen = open;
    });

    effect(() => {
      this.dialogService.messages();
      setTimeout(() => this.scrollToBottom(), 0);
    });

    // Apply a one-shot prefill set by `openWith(...)` — drop it into the composer
    // (editable, not auto-sent) and focus at the end so the user just completes and
    // sends. Cleared via setTimeout so the signal write happens outside the effect.
    effect(() => {
      const prefill = this.dialogService.prefill();
      if (!prefill) return;
      this.draft = prefill;
      setTimeout(() => {
        this.dialogService.consumePrefill();
        const el = this.inputEl?.nativeElement;
        if (el) {
          el.focus();
          el.setSelectionRange(el.value.length, el.value.length);
        }
      }, 0);
    });

    // The textarea is disabled while a turn is in flight, and browsers blur a form
    // control the moment it's disabled. Re-enabling it afterwards doesn't restore
    // focus on its own, so without this the field silently drops focus after every
    // send/confirm/clarify click. Fire only on the sending -> not-sending edge so this
    // doesn't fight the initial-open focus effect above.
    effect(() => {
      const isSending = this.dialogService.status() === 'sending';
      if (this.dialogService.isOpen() && this.wasSending && !isSending) {
        setTimeout(() => this.inputEl?.nativeElement.focus(), 0);
      }
      this.wasSending = isSending;
    });
  }

  protected contextLabel(): string | null {
    if (!this.viewContextService.enabled()) return null;
    return this.viewContextService.describeCurrent();
  }

  /** Maps a message id to a divider label, for messages that start a new opaque
   *  `context` epoch. The raw token is meaningless to look at directly — what's
   *  actually useful for debugging is *when* it changes (or drops to null, meaning
   *  the conversation went terminal), so this turns that into a running "Kontext N"
   *  count instead of repeating the encoded string under every message. */
  protected readonly contextDividers = computed<ReadonlyMap<string, string>>(() => {
    const labels = new Map<string, string>();
    const epochByContext = new Map<string, number>();
    let lastContext: string | null | undefined;
    let nextEpoch = 1;

    for (const message of this.dialogService.messages()) {
      if (!message.debug) continue;
      const context = message.debug.context;
      if (context === lastContext) continue;
      lastContext = context;

      if (context === null) {
        labels.set(message.id, 'Kein Kontext');
        continue;
      }
      let epoch = epochByContext.get(context);
      if (epoch === undefined) {
        epoch = nextEpoch++;
        epochByContext.set(context, epoch);
      }
      labels.set(message.id, `Kontext ${epoch}`);
    }

    return labels;
  });

  /** `null` both when there's no context and when it doesn't parse as a signed `v1`
   *  token (e.g. a mock/dev context) — the template only opens the modal from a
   *  button that's itself gated on a non-null context, so the two cases never need
   *  to be told apart by the caller. */
  protected readonly decodedContext = computed<DecodedContextToken | null>(() => {
    const context = this.dialogService.context();
    return context ? decodeContextToken(context) : null;
  });

  protected readonly contextModalOpen = signal(false);

  protected openContextModal(): void {
    this.turnDetailMessage.set(null);
    this.contextModalOpen.set(true);
  }

  protected closeContextModal(): void {
    this.contextModalOpen.set(false);
  }

  /** Set only for the message currently shown in the turn detail overlay below —
   *  mutually exclusive with `contextModalOpen`, both being debug-only overlays
   *  scoped to this panel. */
  protected readonly turnDetailMessage = signal<ChatMessage | null>(null);

  protected readonly turnDetailRequestJson = computed<string | null>(() => {
    const debug = this.turnDetailMessage()?.debug;
    return debug ? JSON.stringify(debug.request, null, 2) : null;
  });

  protected readonly turnDetailResponseJson = computed<string | null>(() => {
    const debug = this.turnDetailMessage()?.debug;
    return debug ? JSON.stringify(debug.response, null, 2) : null;
  });

  protected openTurnDetail(message: ChatMessage): void {
    this.contextModalOpen.set(false);
    this.turnDetailMessage.set(message);
  }

  protected closeTurnDetail(): void {
    this.turnDetailMessage.set(null);
  }

  /** Escape closes whatever's on top: the turn detail overlay first, then the
   *  context overlay, and the whole panel only once there's nothing left inside
   *  it to close. */
  protected onEscape(): void {
    if (this.turnDetailMessage()) {
      this.closeTurnDetail();
    } else if (this.contextModalOpen()) {
      this.closeContextModal();
    } else {
      this.close();
    }
  }

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void this.submit();
    }
  }

  protected async submit(): Promise<void> {
    const text = this.draft.trim();
    if (!text || this.dialogService.status() === 'sending') return;
    this.draft = '';
    await this.dialogService.send(text);
  }

  protected onOptionSelected(option: ClarifyOption): void {
    void this.dialogService.selectOption(option);
  }

  protected onConfirmAnswered(answer: 'yes' | 'no'): void {
    void this.dialogService.confirmAction(answer);
  }

  protected onRetry(): void {
    void this.dialogService.retryLast();
  }

  protected onSaveRecipeJob(event: {messageId: string; jobId: string}): void {
    void this.dialogService.saveRecipeJobDraft(event.messageId, event.jobId);
  }

  protected onAuthorSuggestion(idea: RecipeSuggestionItem): void {
    void this.dialogService.authorSuggestion(idea);
  }

  protected onCancel(): void {
    void this.dialogService.cancel();
  }

  protected acceptRejoin(): void {
    this.dialogService.acceptRejoin();
  }

  protected dismissRejoin(): void {
    this.dialogService.dismissRejoin();
  }

  protected useExample(phrase: string): void {
    this.draft = phrase;
    setTimeout(() => this.inputEl?.nativeElement.focus(), 0);
  }

  protected close(): void {
    this.dialogService.close();
  }

  protected resetConversation(): void {
    if (this.dialogService.status() === 'sending') return;
    this.dialogService.reset();
    setTimeout(() => this.inputEl?.nativeElement.focus(), 0);
  }

  protected toggleViewContext(value: boolean): void {
    this.viewContextService.setEnabled(value);
  }

  protected toggleDebug(value: boolean): void {
    this.dialogDebug.setEnabled(value);
  }

  private scrollToBottom(): void {
    const el = this.messageListEl?.nativeElement;
    if (el) el.scrollTop = el.scrollHeight;
  }
}
